import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const getAuthToken = () => {
  const token = sessionStorage.getItem('loggedUser');
  const storedUser = JSON.parse(token);
  return storedUser?.result?.token || storedUser?.token || storedUser?.data?.token || storedUser?.user?.token;
};

const BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || 'https://backend.mystore.education/api';

// Fetch banners
export const fetchBanners = createAsyncThunk(
  'banners/fetchBanners',
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }

    
      const response = await axios.get(`${BASE_URL}/sliders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          data: response.data.result || [],
          message: response.data.message || 'Banners fetched successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch banners');
      }
    } catch (error) {
      console.error('❌ Error fetching banners:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error fetching banners');
    }
  }
);

// Add banner
export const addBanner = createAsyncThunk(
  'banners/addBanner',
  async (formData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }

 

      const response = await axios.post(`${BASE_URL}/add_new_slider`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
          'Content-Type': 'multipart/form-data'
        }
      });


      if (response.data.status === 'success') {
        return {
          data: response.data.result,
          message: response.data.message || 'Banner created successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to create banner');
      }
    } catch (error) {
      console.error('❌ Error adding banner:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error creating banner');
    }
  }
);

// Update banner
export const updateBanner = createAsyncThunk(
  'banners/updateBanner',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.post(`${BASE_URL}/update_new_slider/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
          'Content-Type': 'multipart/form-data'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          data: response.data.result,
          message: response.data.message || 'Banner updated successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to update banner');
      }
    } catch (error) {
      console.error('❌ Error updating banner:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error updating banner');
    }
  }
);

// Update banner status
export const updateBannerStatus = createAsyncThunk(
  'banners/updateBannerStatus',
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }



      const response = await axios.post(`${BASE_URL}/update_slide_status/${id}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          message: response.data.message || 'Banner status updated successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to update banner status');
      }
    } catch (error) {
      console.error('❌ Error updating banner status:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error updating banner status');
    }
  }
);

// Delete banner
export const deleteBanner = createAsyncThunk(
  'banners/deleteBanner',
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }



      const response = await axios.delete(`${BASE_URL}/delete_slider/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          message: response.data.message || 'Banner deleted successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to delete banner');
      }
    } catch (error) {
      console.error('❌ Error deleting banner:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error deleting banner');
    }
  }
);

const initialState = {
  data: [],
  loading: false,
  error: null,
  success: false
};

const bannerSlice = createSlice({
  name: 'banners',
  initialState,
  reducers: {
    clearBannerState: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch banners
      .addCase(fetchBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.success = true;
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Add banner
      .addCase(addBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(addBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update banner
      .addCase(updateBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update banner status
      .addCase(updateBannerStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBannerStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateBannerStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Delete banner
      .addCase(deleteBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  }
});

export const { clearBannerState, clearError } = bannerSlice.actions;
export default bannerSlice.reducer;
